import { AxiosRequestConfig } from 'axios'
import {
  GfxRecordsData,
  GfxRequest,
  GfxResponse,
  GfxSpecificConditionsRequest,
  GfxSpecificConditionsResponse,
  GfxSvgRecord,
} from '../api/gfx/interfaces'
import {
  MileageOptions,
  PartImageRequestItem,
  PartImageResponseItem,
  ServiceIntervalsResponse,
} from '../api/searchResults/interfaces'
import {
  ModelResponse,
  SubModelResponse,
  VehicleCclSuggestionResponse,
} from '../api/vehicle/interfaces'
import { TableColumn } from '../features/search/VehicleSearch/interfaces'
import {
  AstItem,
  CategoriesResponse,
  GroupsResponse,
  IdGroupsResponse,
  IdsEnginesResponse,
  IntegrationsInfoResponse,
  IPartItemResponse,
  JobsResponse,
  JobTerminologiesResponse,
  SearchedPartTypesResponse,
} from '../features/partsCatalog/Selections/interfaces'
import { StoreInstances } from '../store/StoreInstancesContainer'
import { IdValueGeneric, IdValueWithRegions } from 'interfaces'
import { LaborItem } from '../store/models/LaborModel'
import { PartType } from '../store/models/PartsCatalogStoreModels'
import { ActiveCatalogPublisher } from '../store/models/PartsCatalogModels'
import {
  OEGFXPartsRequest,
  OeParts,
  PartVehicles,
  PartVehiclesFitmentRequest,
  PartVehiclesResponse,
  SearchLaborResultRequest,
} from '../store/models/SearchModels'
import { SpecificConditionI } from '../store/models/SpecificConditions'
import { VehicleSpecification } from '../store/models/VehicleSpecification'
import { Vehicle } from '../store/models/Vehicles'
import { CatalogNode } from '../store/partsCatalog/CatalogNode'
import { PartsCatalogType } from '../store/partsCatalog/PartsCatalog'
import { BaseServiceProvider } from './BaseServiceProvider'
import PartServiceProvider from './PartServiceProvider'
import { CrossReferenceResponse } from '../api/crossReference/interface'
import { getSavedVehicleRegion } from 'libs/cart/src/helpers/vehicleDataStorage'

interface MakeFilterStatusResponse {
  filterStatus: boolean
}

interface SelectedMakesResponse {
  selectedMakes: string[]
}

class CatalogServiceProvider extends BaseServiceProvider {
  constructor() {
    super(import.meta.env.VITE_API_BASE_URL, 'catalog', '1.2')
  }

  getGFXSpecificConditions = async (
    body: GfxSpecificConditionsRequest
  ): Promise<GfxRecordsData | undefined> => {
    const resp = await this.post<GfxSpecificConditionsResponse>(
      'gfxSpecificConditions',
      body,
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    if (resp.status === 200) {
      return resp?.data?.gfxRecords
    }
    if (resp.status === 204) {
      throw new Error('No specific conditions records')
    }
    return resp?.data?.gfxRecords
  }

  getGFX = async (body: GfxRequest): Promise<GfxSvgRecord> => {
    const resp = await this.post<GfxResponse>('gfx', body, {
      params: {
        ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
      },
    })
    return resp?.data?.gfxsvgRecord
  }

  getPartImages = async (
    partImageRequestBody: Array<PartImageRequestItem>
  ): Promise<Array<PartImageResponseItem>> => {
    try {
      const resp = await this.post<Array<PartImageResponseItem>>(
        'part/images',
        partImageRequestBody
      )
      if (resp.status === 200) return resp.data
    } catch (_error) {
      return null
    }

    return null
  }

  getLaborSearchResults = async (
    reqData: SearchLaborResultRequest
  ): Promise<LaborItem[]> => {
    const resp = await this.post<LaborItem[]>('laborResult', reqData, {
      params: {
        partDomain: PartServiceProvider.getPartDomain(),
        ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
      },
    })
    return resp.data
  }

  getGfxOeParts = async (reqData: OEGFXPartsRequest): Promise<OeParts> => {
    const resp = await this.post<OeParts>('part/oe', reqData, {
      params: {
        partDomain: PartServiceProvider.getPartDomain(),
      },
    })
    return resp.data
  }

  getPartVehiclesFitmentInterchange = async (
    reqData: PartVehiclesFitmentRequest
  ): Promise<PartVehicles> => {
    const resp = await this.post<PartVehiclesResponse>(
      `part/vehicles`,
      reqData,
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )

    return {
      ...resp.data,
      manufacturers: (resp.data.manufacturers || []).map(
        (manufacturer, index) => ({ id: index, value: manufacturer })
      ),
    }
  }

  getServiceIntervals = async (
    requestParameter: { intervalType: number; severeCode: string },
    requestBody: Vehicle
  ): Promise<Array<MileageOptions>> => {
    const resp = await this.post<MileageOptions[]>(
      `serviceIntervals?intervalType=${requestParameter.intervalType}&severeCode=${requestParameter.severeCode}`,
      requestBody,
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    if (resp.status === 200) return resp.data

    return null
  }

  getRecommendedServices = async (
    requestParameter: {
      intervalType: number
      severeCode: string
      mileage: string
    },
    requestBody: Vehicle
  ): Promise<Array<ServiceIntervalsResponse>> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    const resp = await this.post<any>(
      `serviceIntervalsData?intervalType=${requestParameter.intervalType}&severeCode=${requestParameter.severeCode}&mileage=${requestParameter.mileage}`,
      requestBody,
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    if (resp.status === 200) return resp.data
    return null
  }

  public beforeEachRequest(config: AxiosRequestConfig): AxiosRequestConfig {
    return {
      ...config,
      params: {
        catalogId:
          StoreInstances.searchStore?.currentVehicle?.type?.id === undefined
            ? 111
            : StoreInstances.searchStore.currentVehicle.type.id,
        covListId: StoreInstances.userStore?.preferences?.mclOrgId,
        countryId: StoreInstances.userStore?.country?.countryCode,
        mclOrgId: StoreInstances.userStore?.preferences?.mclOrgId,
        ...config.params,
      },
    }
  }

  getSpecificConditions = async (
    vehicle: Vehicle,
    terminologies: Array<PartType>,
    catalogType: PartsCatalogType,
    serviceIntervalParams?: {
      intervalType: number
      severeCode: string
      mileage: number
    }
  ): Promise<Array<SpecificConditionI>> => {
    const endPoint = this.getEndpoint(catalogType)
    const resp = await this.post<Array<SpecificConditionI>>(
      endPoint,
      {
        ...(catalogType === PartsCatalogType.SERVICE_INTERVAL_SEARCH
          ? {
              ...vehicle,
            }
          : { vehicle, terminologies }),
      },
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
          ...(catalogType === PartsCatalogType.SERVICE_INTERVAL_SEARCH &&
            serviceIntervalParams),
        },
      }
    )
    if (resp.status === 200) {
      return resp.data.map((question) => ({
        ...question,
        source: catalogType,
      }))
    }
    if (resp.status === 204) {
      return []
    }
    return []
  }

  getEndpoint = (catalogType: PartsCatalogType): string => {
    switch (catalogType) {
      case PartsCatalogType.LABOR_SEARCH: {
        return 'laborSpecificConditions'
      }
      case PartsCatalogType.SERVICE_INTERVAL_SEARCH: {
        return 'siSpecificConditions'
      }
      default: {
        return 'specificConditions'
      }
    }
  }

  /**
   * Helper method to generate an empty column structure
   * @param numCols
   */
  initTableCols = (numCols) => {
    const cols = []
    for (let i = 0; i < numCols; i += 1) {
      cols.push({
        id: `${i}`,
        header: '',
        options: [],
      })
    }
    return cols
  }

  /**
   * Returns TableColumns for all vehicle years, sorted and arranged by decade
   */
  getYearOptions = async (): Promise<Array<TableColumn>> => {
    let yearList = []
    try {
      const resp = await this.post<Array<IdValueWithRegions<number, string>>>(
        'years',
        undefined,
        {
          params: {
            catalogId:
              StoreInstances.searchStore.currentFieldsSelections.type?.id,
          },
        }
      )
      yearList = resp.data
    } catch (_e) {
      return []
    }

    // Split by decade
    const minYear = Math.min(...yearList.map((pair) => pair.value)) // Oldest year in the list
    const maxYear = Math.max(...yearList.map((pair) => pair.value)) // Latest year in the list

    // Calculate the number of decades and initialize columns
    const numBuckets = Math.floor((maxYear - minYear) / 10) + 1

    // Initialize columns dynamically based on the number of decades
    const cols: Array<TableColumn> = this.initTableCols(numBuckets)
    yearList.forEach((pair) => {
      const bucketIndex = Math.floor((pair.value - minYear) / 10)

      // Ensure the bucket index is valid and add the year to the correct bucket
      if (bucketIndex >= 0 && bucketIndex < cols.length) {
        cols[bucketIndex].options.push({
          value: pair.value,
          id: pair.id,
          regionIds: pair?.regionIds,
        })
      }
    })
    cols.reverse()

    return cols
  }

  getMakesByYear = async (
    year?: IdValueGeneric<number, string> & { regionIds?: number[] }
  ): Promise<
    Array<IdValueGeneric<number, string> & { regionIds?: number[] }>
  > => {
    type returnType = Array<
      IdValueGeneric<number, string> & { regionIds?: number[] }
    >
    if (year && !year.regionIds) {
      const yearOptions = await this.getYearOptions()
      const yearWithRegions = yearOptions
        .flatMap((col) => col.options)
        .find((opt) => opt.id === year.id)
      if (yearWithRegions?.regionIds) {
        year = { ...year, regionIds: yearWithRegions.regionIds }
      }
    }

    const body = year
      ? {
          year: { id: year.id, value: year.value },
          ...(StoreInstances.userStore.isCclEnabled &&
            year.regionIds && { regionIds: year.regionIds }),
        }
      : {}

    const config = {
      params: {
        catalogId: StoreInstances.searchStore.currentFieldsSelections.type?.id,
      },
    }
    try {
      const resp = await this.post<returnType>('makes', body, config)
      return resp.data || []
    } catch (_e) {
      return []
    }
  }

  getAllMakes = async (): Promise<Array<IdValueGeneric<number, string>>> => {
    type IdValueArray = Array<IdValueGeneric<number, string>>
    try {
      const resp = await this.get<IdValueArray>('getAllMakes')
      return resp.data || []
    } catch (_e) {
      return []
    }
  }

  getCustomMakes = async (
    year?: IdValueGeneric<number, string>
  ): Promise<Array<IdValueGeneric<number, string>>> => {
    type returnType = Array<IdValueGeneric<number, string>>
    const query = `getFilteredMakes?year=${year?.id}`
    try {
      const resp = await this.get<returnType>(query)
      return resp.data || []
    } catch (_e) {
      return []
    }
  }

  getSelectedMakes = async (): Promise<string[]> => {
    const endpoint = `getSelectedMakes`
    try {
      const resp = await this.get<SelectedMakesResponse>(endpoint)
      const selectedMakes = resp.data?.selectedMakes
      const isEmpty = selectedMakes?.[0] === '' && selectedMakes?.length === 1
      if (isEmpty) return []
      return selectedMakes
    } catch (_e) {
      return []
    }
  }

  isMakesFilterEnabled = async (): Promise<boolean> => {
    try {
      const resp = await this.get<MakeFilterStatusResponse>(
        'getMakeFilterStatus'
      )
      return resp.data?.filterStatus
    } catch (_e) {
      return false // assume false
    }
  }

  updateMakesFilterStatus = async (status: boolean): Promise<void> => {
    const body = {
      filterStatus: status,
    }
    await this.patch<Response>('updateMakeFilterStatus', body)
  }

  /**
   * Returns a list of TableColumns of make options given a list of makes
   * @param makes
   * @returns
   */
  formatMakeOptions = (
    makes: Array<IdValueGeneric<number, string> & { regionIds?: number[] }>
  ): Array<TableColumn> => {
    const sortedMakes = makes.sort((a, b) => a.value.localeCompare(b.value))
    const numOfColumns = sortedMakes?.length > 10 ? 4 : 1
    const cols: Array<TableColumn> = this.initTableCols(numOfColumns)
    const maxInCol = Math.ceil(sortedMakes.length / cols.length)
    sortedMakes.forEach((make, index) => {
      const bucket = Math.floor(index / maxInCol)
      cols[bucket].options.push({
        value: make.value,
        id: make.id,
        ...(make.regionIds && { regionIds: make.regionIds }),
      })
    })

    return cols
  }

  /**
   * Returns a list of TableColumns of make options given the currently selected year
   * @param vehicle
   */
  getMakeOptions = async (
    vehicle: Vehicle,
    filterMakes: boolean
  ): Promise<Array<TableColumn>> => {
    const getCustomMakes = async () => await this.getCustomMakes(vehicle.year)
    const getMakes = async () => await this.getMakesByYear(vehicle.year)
    const getMakesList = filterMakes ? getCustomMakes : getMakes
    const makeList = await getMakesList()
    return this.formatMakeOptions(makeList)
  }

  /**
   * Returns a list of TableColumns of all available make options
   */
  getAllMakeOptions = async (): Promise<Array<TableColumn>> => {
    const makeList = await this.getAllMakes()
    return this.formatMakeOptions(makeList)
  }

  /**
   * Returns a list of IdValue of make options given the currently selected year
   * @param vehicle
   */
  getIdValueMakeOptions = async (
    vehicle: Vehicle
  ): Promise<Array<IdValueGeneric<number, string>>> => {
    /* Formatting data for api (key -> id) with no param reassign */
    const body = {
      year: {
        id: vehicle.year.id,
        value: vehicle.year.value,
      },
    }
    let makeList = []
    try {
      const resp = await this.post<Array<IdValueGeneric<number, string>>>(
        'makes',
        body
      )
      makeList = resp.data
      return makeList
    } catch (_e) {
      return []
    }
  }

  /**
   * Returns a list of TableColumns for the models available for the currently selected make
   * @param vehicle
   */
  getModelOptions = async (vehicle: Vehicle): Promise<Array<TableColumn>> => {
    /* Formatting data for api (key -> id) with no param reassign */
    let makeWithRegions = vehicle?.make as IdValueGeneric<number, string> & {
      regionIds?: number[]
    }

    if (makeWithRegions && !makeWithRegions.regionIds) {
      const makeOptions = await this.getMakesByYear(vehicle.year)
      const makeWithRegionsFromApi = makeOptions.find(
        (opt) => opt.id === makeWithRegions.id
      )
      if (makeWithRegionsFromApi?.regionIds) {
        makeWithRegions = {
          ...makeWithRegions,
          regionIds: makeWithRegionsFromApi.regionIds,
        }
      }
    }
    const body = {
      year: {
        id: vehicle?.year?.id,
        value: vehicle?.year?.value,
      },
      make: {
        id: vehicle?.make?.id,
        value: vehicle?.make?.value,
      },
      ...(StoreInstances.userStore.isCclEnabled &&
        makeWithRegions?.regionIds && {
          regionIds: makeWithRegions.regionIds,
        }),
    }

    let modelTypes
    try {
      const resp = await this.post<Array<ModelResponse>>('models', body, {
        params: {
          catalogId:
            StoreInstances.searchStore.currentFieldsSelections.type?.id,
        },
      })
      modelTypes = resp.data
    } catch (_e) {
      return []
    }

    const cols = this.initTableCols(modelTypes.length)
    modelTypes.forEach((modelType: ModelResponse, index) => {
      const { models } = modelType
      cols[index].id = modelType.modelTypeId
      cols[index].header = modelType.value
      cols[index].options = models
    })
    return cols
  }

  /**
   * Returns a list of TableColumns for the sub-models available for the currently selected model
   * @param vehicle
   */
  getSubModelOptions = async (
    vehicle: Vehicle
  ): Promise<Array<TableColumn>> => {
    /* Formatting data for api (key -> id) with no param reassign */
    let modelWithRegions = vehicle?.model as IdValueGeneric<number, string> & {
      regionIds?: number[]
    }

    if (modelWithRegions && !modelWithRegions.regionIds) {
      const modelOptions = await this.getModelOptions(vehicle)
      const modelWithRegionsFromApi = modelOptions
        .flatMap((col) => col.options)
        .find((opt) => opt.id === modelWithRegions.id)
      if (modelWithRegionsFromApi?.regionIds) {
        modelWithRegions = {
          ...modelWithRegions,
          regionIds: modelWithRegionsFromApi.regionIds,
        }
      }
    }

    const body = {
      year: {
        id: vehicle?.year?.id,
        value: vehicle?.year?.value,
      },
      make: {
        id: vehicle?.make?.id,
        value: vehicle?.make?.value,
      },
      model: {
        id: vehicle?.model?.id,
        value: vehicle?.model?.value,
      },
      modelType: {
        id: vehicle?.modelType?.id,
        value: vehicle?.modelType?.value,
      },
      ...(StoreInstances.userStore.isCclEnabled &&
        modelWithRegions?.regionIds && {
          regionIds: modelWithRegions.regionIds,
        }),
    }

    try {
      const resp = await this.post<Array<SubModelResponse>>('submodels', body, {
        params: {
          catalogId:
            StoreInstances.searchStore.currentFieldsSelections.type?.id,
        },
      })

      const cols: Array<TableColumn> = this.initTableCols(1)
      cols[0].options = resp.data
      return cols
    } catch (_e) {
      return []
    }
  }

  /**
   * Returns a list of IdValue for the models available for the currently selected make
   * @param vehicle
   */
  getIdValueModelOptions = async (
    vehicle: Vehicle
  ): Promise<Array<ModelResponse>> => {
    /* Formatting data for api (key -> id) with no param reassign */
    const body = {
      year: {
        id: vehicle.year.id,
        value: vehicle.year.value,
      },
      make: {
        id: vehicle.make.id,
        value: vehicle.make.value,
      },
    }
    let modelTypes
    try {
      const resp = await this.post<Array<ModelResponse>>('models', body)
      modelTypes = resp.data
      return modelTypes
    } catch (_e) {
      return []
    }
  }

  /**
   * Returns a single TableColumn containing a list of engine options for the currently selected model
   * @param vehicle
   */
  getEngineOptions = async (vehicle: Vehicle): Promise<Array<TableColumn>> => {
    let subModelWithRegions = vehicle?.subModel as IdValueGeneric<
      number,
      string
    > & {
      regionIds?: number[]
    }
    let modelWithRegions = vehicle?.model as IdValueGeneric<number, string> & {
      regionIds?: number[]
    }

    if (
      vehicle?.subModel &&
      subModelWithRegions &&
      !subModelWithRegions.regionIds
    ) {
      const subModelOptions = await this.getSubModelOptions(vehicle)
      const subModelWithRegionsFromApi = subModelOptions[0]?.options.find(
        (opt) => opt.id === subModelWithRegions.id
      )
      if (subModelWithRegionsFromApi?.regionIds) {
        subModelWithRegions = {
          ...subModelWithRegions,
          regionIds: subModelWithRegionsFromApi.regionIds,
        }
      }
    }

    if (modelWithRegions && !modelWithRegions.regionIds) {
      const modelOptions = await this.getModelOptions(vehicle)
      const modelWithRegionsFromApi = modelOptions
        .flatMap((col) => col.options)
        .find((opt) => opt.id === modelWithRegions.id)
      if (modelWithRegionsFromApi?.regionIds) {
        modelWithRegions = {
          ...modelWithRegions,
          regionIds: modelWithRegionsFromApi.regionIds,
        }
      }
    }

    const body = {
      ...vehicle,
      initialized: undefined,
      ...(StoreInstances.userStore.isCclEnabled &&
        (subModelWithRegions?.regionIds
          ? { regionIds: subModelWithRegions.regionIds }
          : modelWithRegions?.regionIds && {
              regionIds: modelWithRegions.regionIds,
            })),
    }

    let engineList
    try {
      const resp = await this.post<Array<IdValueGeneric<number, string>>>(
        'engines',
        body,
        {
          params: {
            catalogId:
              StoreInstances.searchStore.currentFieldsSelections.type?.id,
          },
        }
      )
      engineList = resp.data
    } catch (_e) {
      return [] // @TODO: Add better error handling... might want to let the exception propagate
    }
    const cols = this.initTableCols(1)
    cols[0].options = engineList

    return cols
  }

  /**
   * Returns a single IdValue containing a list of engine options for the currently selected model
   * @param vehicle
   */
  getIdValueEngineOptions = async (
    vehicle: Vehicle
  ): Promise<Array<IdValueGeneric<number, string>>> => {
    const body = { ...vehicle, initialized: undefined }
    let engineList
    try {
      const resp = await this.post<Array<IdValueGeneric<number, string>>>(
        'engines',
        body
      )
      engineList = resp.data
      return engineList
    } catch (_e) {
      return [] // @TODO: Add better error handling... might want to let the exception propagate
    }
  }

  /**
   * Returns a single TableColumn containing a list of engine options for the currently selected model
   * @param vehicle
   */
  getEngineOptionsPlain = async (
    vehicle: Vehicle
  ): Promise<Array<TableColumn>> => {
    const body = { ...vehicle, initialized: undefined }
    let engineList
    try {
      const resp = await this.post<Array<IdValueGeneric<number, string>>>(
        'engines',
        body
      )
      engineList = resp.data.filter((item) => item.value !== 'All Engine')
    } catch (_e) {
      return [] // @TODO: Add better error handling... might want to let the exception propagate
    }
    return engineList
  }

  updateCustomMakes = async (makes: string): Promise<void> => {
    await this.post<string>('updateFilteredMakes', { filteredMakes: makes })
  }

  fetchCategories = async (
    vehicle?: Vehicle
  ): Promise<Array<IdValueGeneric<string, string>>> => {
    const resp = await this.post<Array<IdValueGeneric<string, string>>>(
      'categories',
      vehicle,
      {
        params: {
          catalogId: StoreInstances.searchStore.currentVehicle?.type
            ? StoreInstances.searchStore.currentVehicle?.type.id
            : 111,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  fetchJobs = async (
    categories: Array<IdValueGeneric<string, string>>
  ): Promise<JobsResponse> => {
    const resp = await this.post<JobsResponse>(
      'jobs',
      {
        categories,
      },
      {
        params: {
          catalogId: StoreInstances.searchStore.currentVehicle?.type
            ? StoreInstances.searchStore.currentVehicle?.type.id
            : 111,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  fetchJobTerminologies = async (
    catalogNode?: CatalogNode[]
  ): Promise<JobTerminologiesResponse> => {
    const newGroups = catalogNode.map((node) => ({
      jobId: Number(node.id),
      categoryId: Number(node.getParent().id),
    }))
    const resp = await this.post<JobTerminologiesResponse>(
      'jobTerminologies',
      newGroups,
      {
        params: {
          catalogId: StoreInstances.searchStore.currentVehicle?.type
            ? StoreInstances.searchStore.currentVehicle?.type.id
            : 111,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  fetchGroups = async (
    categories: Array<IdValueGeneric<string, string>>,
    vehicle?: Vehicle
  ): Promise<IdGroupsResponse> => {
    const resp = await this.post<IdGroupsResponse>(
      'groups',
      {
        categories,
        vehicle,
      },
      {
        params: {
          catalogId: StoreInstances.searchStore.currentVehicle?.type
            ? StoreInstances.searchStore.currentVehicle?.type.id
            : 111,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  fetchTerminologies = async (
    groups: Array<IdValueGeneric<string, string>>,
    vehicle?: Vehicle
  ): Promise<IPartItemResponse | IdsEnginesResponse> => {
    const resp = await this.post<IPartItemResponse | IdsEnginesResponse>(
      'terminologies',
      {
        groups,
        vehicle,
      },
      {
        params: {
          catalogId: StoreInstances.searchStore.currentVehicle?.type
            ? StoreInstances.searchStore.currentVehicle?.type.id
            : 111,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  fetchVehicleSpecifications = async (currentVehicle) => {
    const resp = await this.post<Array<VehicleSpecification>>(
      'part/specifications',
      {
        ...currentVehicle,
      },
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp
  }

  fetchAstBrands = async () => {
    const resp = await this.get<AstItem[]>('astBrands')
    return resp
  }

  fetchAstCategories = async () => {
    const resp = await this.get<AstItem[]>('astCategories')
    return resp
  }

  fetchAstGroups = async (endcodedCategory) => {
    const urlSuffix = `astGroups?category=${endcodedCategory}`
    const resp = await this.get<AstItem[]>(urlSuffix)
    return resp
  }

  fetchAstTerminologies = async (encodedGroup) => {
    const urlSuffix = `astTerminologies?group=${encodedGroup}`
    const resp = await this.get<AstItem[]>(urlSuffix)
    return resp
  }

  fetchAstIntegrationInfo = async (parameters) => {
    const resp = await this.get<IntegrationsInfoResponse>('integrationsInfo', {
      params: parameters,
    })
    return resp
  }

  fetchLaborCategories = async (
    vehicle?: Vehicle,
    partGroupId?: string
  ): Promise<CategoriesResponse> => {
    const resp = await this.post<CategoriesResponse>(
      'selectedLaborCategories',
      vehicle,
      {
        params: {
          catalogId: StoreInstances.searchStore.currentVehicle?.type
            ? StoreInstances.searchStore.currentVehicle?.type.id
            : 111,
          partgroupId: partGroupId,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  fetchLaborGroups = async (
    categories: Array<IdValueGeneric<string, string>>,
    vehicle?: Vehicle,
    partGroupId?: string
  ): Promise<GroupsResponse> => {
    const resp = await this.post<GroupsResponse>(
      'selectedLaborGroups',
      {
        categories,
        vehicle,
      },
      {
        params: {
          catalogId: StoreInstances.searchStore.currentVehicle?.type
            ? StoreInstances.searchStore.currentVehicle?.type.id
            : 111,
          partgroupId: partGroupId,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  fetchLaboeTerminologies = async (
    groups: Array<IdValueGeneric<string, string>>,
    vehicle?: Vehicle
  ): Promise<IPartItemResponse | IdsEnginesResponse> => {
    const resp = await this.post<IPartItemResponse | IdsEnginesResponse>(
      'laborDescriptions',
      {
        groups,
        vehicle,
      },
      {
        params: {
          catalogId: StoreInstances.searchStore.currentVehicle?.type
            ? StoreInstances.searchStore.currentVehicle?.type.id
            : 111,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  fetchSearchedPartTypes = (
    searchTerm: string
  ): Promise<Array<SearchedPartTypesResponse>> => {
    return this.post<Array<SearchedPartTypesResponse>>(
      'searchTerminologies',
      {
        searchTerm,
        vehicle: {
          year: StoreInstances.searchStore.currentVehicle.year,
          make: StoreInstances.searchStore.currentVehicle.make,
          model: StoreInstances.searchStore.currentVehicle.model,
          modelType: StoreInstances.searchStore.currentVehicle.modelType,
          engine: StoreInstances.searchStore.currentVehicle.engine,
          ...(StoreInstances.searchStore.currentVehicle?.subModel?.value
            ? {
                subModel: StoreInstances.searchStore.currentVehicle.subModel,
              }
            : {}),
        },
      },
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    ).then((resp) => resp.data)
  }

  getCrossReferenceData = async (payload): Promise<CrossReferenceResponse> => {
    try {
      const resp = await this.post<CrossReferenceResponse>(
        'part/interchange/crossReference',
        payload
      )
      if (resp.status === 200) {
        const newData = {
          ...resp.data,
          crossReferenceData: (resp.data.crossReferenceData || []).map(
            (part) => ({
              ...part,
              partNumber: part.partNumber ?? '',
              manufacturer: part.manufacturer ?? '',
              description: part.description ?? '',
            })
          ),
        }
        return newData
      }
      if (resp.status === 204) {
        return {
          crossReferenceData: [],
          filters: [],
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (error: any) {
      if (error?.message === 'Request failed with status code 400') {
        throw new Error('searchTermShouldBeMinimum2Characters')
      } else {
        throw new Error(error.toString())
      }
    }
    return {
      crossReferenceData: [],
      filters: [],
    }
  }

  getActiveCatalogPublisher = async (): Promise<ActiveCatalogPublisher> => {
    const resp = await this.get<ActiveCatalogPublisher>(
      'activeCatalogPublisher'
    )
    return resp.data
  }

  getVehicleCclSuggestions = async (
    searchTerm: string
  ): Promise<VehicleCclSuggestionResponse> => {
    try {
      const queryParams = {
        catalogId: StoreInstances.searchStore.currentFieldsSelections?.type?.id,
        searchTerm,
      }
      const resp = await this.post<VehicleCclSuggestionResponse>(
        '/vehicle/ccl/suggestions',
        {},
        { params: { ...queryParams } }
      )
      if (resp.status === 200) {
        return resp.data
      }
    } catch {
      return null
    }
    return null
  }
}

export default new CatalogServiceProvider()
